"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.zodDateTimeSchema = exports.zodDateTimeWireSchema = exports.zodTypeguardArrayFunctionLogWrapper = exports.zodTypeguardErrorWrapper = exports.zodTypeguardFunctionLogWrapper = exports.zodTypeguardFunctionWrapper = exports.zodValidateOrThrow = exports.getZodValidationErrors = exports.zodTypeGuardWithErrors = exports.zodArrayTypeGuard = exports.zodTypeGuard = exports.hasCreatedAtAndUpdatedAtJsDate = exports.typeguardArrayFunctionLogWrapper = exports.typeguardFunctionLogWrapper = exports.typeguardErrorWrapper = exports.throwingTypeguardWrapper = exports.typeguardFunctionWrapper = void 0;
const jsonschema_1 = __importDefault(require("jsonschema"));
const zod_1 = require("zod");
const Log_1 = require("../types/Log");
const typeguardFunctionWrapper = (schema) => (obj, onErrors) => {
    const { valid, errors } = jsonschema_1.default.validate(obj, schema, {
        nestedErrors: true,
    });
    if (errors.length > 0 && onErrors) {
        onErrors(errors);
    }
    return valid;
};
exports.typeguardFunctionWrapper = typeguardFunctionWrapper;
/**
 * Creates a typeguard which throws on validation errors.
 * This wraps typeguardFunctionWrapper to provide error-throwing behavior.
 *
 * @param schema - A JSON Schema object (not a Zod schema)
 * @returns A TypeGuard function that throws an Error with validation messages on failure
 */
const throwingTypeguardWrapper = (schema) => {
    const basicGuard = (0, exports.typeguardFunctionWrapper)(schema);
    return (obj) => {
        let errors = [];
        const isValid = basicGuard(obj, (errs) => {
            errors = errs;
        });
        if (errors.length) {
            throw new Error(`${errors.map((error) => error.stack)}`);
        }
        return isValid;
    };
};
exports.throwingTypeguardWrapper = throwingTypeguardWrapper;
const _manageTypeguardLogError = (params) => {
    const { typeguardName, logInputs, errors } = params;
    const { logLevel, logger, metadata } = logInputs ?? {};
    if (logger) {
        const errorMessages = errors.map(({ stack }) => stack).join(', ');
        const errObj = {
            message: `"${typeguardName}" typeguard incurred ${errors.length} errors: ${errorMessages}`,
            metadata: { logMeta: { ...metadata } },
        };
        logger(errObj, logLevel || Log_1.LogLevel.Error);
    }
};
/**
 * This util just returns back the associated typeguard errors
 */
const typeguardErrorWrapper = (params) => (obj) => {
    const { schema } = params;
    const { errors } = jsonschema_1.default.validate(obj, schema, {
        nestedErrors: true,
    });
    return errors.map((err) => err.stack);
};
exports.typeguardErrorWrapper = typeguardErrorWrapper;
const typeguardFunctionLogWrapper = (params) => (obj, logInputs) => {
    const { schema, typeguardName } = params;
    return (0, exports.typeguardFunctionWrapper)(schema)(obj, (errors) => {
        _manageTypeguardLogError({ logInputs, errors, typeguardName });
    });
};
exports.typeguardFunctionLogWrapper = typeguardFunctionLogWrapper;
const typeguardArrayFunctionLogWrapper = (params) => (obj, logInputs) => {
    const { schema, typeguardName } = params;
    if (!Array.isArray(obj)) {
        if (logInputs?.logger) {
            const errObj = {
                message: `"${typeguardName}" typeguard expected array input`,
            };
            logInputs.logger(errObj, logInputs.logLevel || Log_1.LogLevel.Error);
        }
        return false;
    }
    let isValid = true;
    const errors = [];
    for (const item of obj) {
        const jsonResult = jsonschema_1.default.validate(item, schema, {
            nestedErrors: true,
        });
        jsonResult.errors.forEach((err) => errors.push(err));
        isValid = isValid && jsonResult.valid;
    }
    if (errors.length > 0) {
        _manageTypeguardLogError({ logInputs, errors, typeguardName });
        return false;
    }
    return isValid;
};
exports.typeguardArrayFunctionLogWrapper = typeguardArrayFunctionLogWrapper;
// jsonschema types JsDate object as string, with date-time annotation,
// we need a translation layer when typeguarding types containing JsDate
const hasCreatedAtAndUpdatedAtJsDate = ({ obj, typeName, logInputs, }) => {
    const { logLevel, logger, metadata } = logInputs ?? {};
    if (typeof obj !== 'object') {
        if (logger) {
            logger({
                message: `Expected ${typeName} to be typeof object, but got [type:${typeof obj}]`,
                metadata: { logMeta: metadata },
            }, logLevel ?? Log_1.LogLevel.Error);
        }
        return false;
    }
    if (!('created_at' in obj && obj.created_at instanceof Date) ||
        !('updated_at' in obj && obj.updated_at instanceof Date)) {
        if (logger) {
            logger({
                message: `Expected ${typeName} to have properties created_at and updated_at of type JsDate object.`,
                metadata: { logMeta: metadata },
            }, logLevel ?? Log_1.LogLevel.Error);
        }
        return false;
    }
    return true;
};
exports.hasCreatedAtAndUpdatedAtJsDate = hasCreatedAtAndUpdatedAtJsDate;
/**
 * ============================================================================
 * ZOD TYPEGUARD UTILITIES
 * ============================================================================
 * These utilities provide similar functionality to the jsonschema typeguard
 * wrappers above, but using Zod for validation. When the migration from
 * jsonschema to Zod is complete, the jsonschema utilities above can be removed.
 */
/**
 * Creates a type guard function from a Zod schema
 *
 * @param schema - The Zod schema to validate against
 * @returns A type guard function that validates and narrows the type
 *
 * @example
 * const isGetTablesResponse = zodTypeGuard(GetTablesResponseSchema);
 * if (isGetTablesResponse(data)) {
 *   // data is now typed as GetTablesResponse
 * }
 * if (isGetTablesResponse(data, { logger: logError })) {
 *   // data is now typed as GetTablesResponse, errors logged
 * }
 */
const zodTypeGuard = (schema) => {
    return (data, logOptions) => {
        const result = schema.safeParse(data);
        if (!result.success && logOptions?.logger) {
            const { logger, logLevel, metadata } = logOptions;
            const errorMessages = result.error.issues
                .map((issue) => {
                const path = issue.path.join('.');
                return `${path ? `${path}: ` : ''}${issue.message}`;
            })
                .join(', ');
            logger({
                message: `Zod validation failed: ${errorMessages}`,
                metadata: { logMeta: { ...metadata } },
            }, logLevel || Log_1.LogLevel.Error);
        }
        return result.success;
    };
};
exports.zodTypeGuard = zodTypeGuard;
/**
 * Creates a type guard function for arrays from a Zod schema
 *
 * @param schema - The Zod schema to validate array elements against
 * @returns A type guard function that validates arrays
 *
 * @example
 * const isFundingComponentArray = zodArrayTypeGuard(FundingSubmissionComponentSchema);
 * if (isFundingComponentArray(data)) {
 *   // data is now typed as FundingSubmissionComponent[]
 * }
 */
const zodArrayTypeGuard = (schema) => {
    const arraySchema = zod_1.z.array(schema);
    return (data, logOptions) => {
        const result = arraySchema.safeParse(data);
        if (!result.success && logOptions?.logger) {
            const { logger, logLevel, metadata } = logOptions;
            const errorMessages = result.error.issues
                .map((issue) => {
                const path = issue.path.join('.');
                return `${path ? `${path}: ` : ''}${issue.message}`;
            })
                .join(', ');
            logger({
                message: `Zod array validation failed: ${errorMessages}`,
                metadata: { logMeta: { ...metadata } },
            }, logLevel || Log_1.LogLevel.Error);
        }
        return result.success;
    };
};
exports.zodArrayTypeGuard = zodArrayTypeGuard;
/**
 * Creates a Zod type guard with error callback
 *
 * @param schema - The Zod schema to validate against
 * @param onError - Callback function to handle validation errors
 * @returns A type guard function
 *
 * @example
 * const isValid = zodTypeGuardWithErrors(
 *   GetTablesResponseSchema,
 *   (errors) => console.error('Validation failed:', errors)
 * );
 */
const zodTypeGuardWithErrors = (schema, onError) => {
    return (data) => {
        const result = schema.safeParse(data);
        if (!result.success && onError) {
            onError(result.error);
        }
        return result.success;
    };
};
exports.zodTypeGuardWithErrors = zodTypeGuardWithErrors;
/**
 * Validates data using Zod and returns formatted error messages
 *
 * @param schema - The Zod schema to validate against
 * @param data - The data to validate
 * @returns Array of error messages, empty if valid
 *
 * @example
 * const errors = getZodValidationErrors(GetTablesResponseSchema, data);
 * if (errors.length > 0) {
 *   console.error('Validation errors:', errors);
 * }
 */
const getZodValidationErrors = (schema, data) => {
    const result = schema.safeParse(data);
    if (result.success) {
        return [];
    }
    if (!result.error || !result.error.issues) {
        return ['Unknown validation error'];
    }
    return result.error.issues.map((err) => {
        const path = err.path.join('.');
        return `${path ? `${path}: ` : ''}${err.message}`;
    });
};
exports.getZodValidationErrors = getZodValidationErrors;
/**
 * Validates data using Zod and throws with formatted error message if invalid
 *
 * @param schema - The Zod schema to validate against
 * @param data - The data to validate
 * @param errorPrefix - Optional prefix for error message
 * @returns The validated and typed data
 * @throws {Error} If validation fails
 *
 * @example
 * try {
 *   const validData = zodValidateOrThrow(GetTablesResponseSchema, data, 'API Response');
 *   // validData is now typed as GetTablesResponse
 * } catch (error) {
 *   console.error(error.message);
 * }
 */
const zodValidateOrThrow = (schema, data, errorPrefix) => {
    const result = schema.safeParse(data);
    if (result.success) {
        return result.data;
    }
    if (!result.error || !result.error.issues) {
        const prefix = errorPrefix ? `${errorPrefix}: ` : '';
        throw new Error(`${prefix}Validation failed: Unknown error`);
    }
    const errors = result.error.issues
        .map((err) => {
        const path = err.path.join('.');
        return `${path ? `${path}: ` : ''}${err.message}`;
    })
        .join(', ');
    const prefix = errorPrefix ? `${errorPrefix}: ` : '';
    throw new Error(`${prefix}Validation failed: ${errors}`);
};
exports.zodValidateOrThrow = zodValidateOrThrow;
/**
 * Wrapper for Zod schema that matches the interface of typeguardFunctionWrapper (BasicTypeGuard).
 * Use this when migrating from jsonschema to Zod typeguards that need error callbacks.
 */
const zodTypeguardFunctionWrapper = (schema) => (obj, onErrors) => {
    const result = schema.safeParse(obj);
    if (!result.success && onErrors) {
        // Convert Zod errors to jsonschema-compatible error format for backward compatibility
        const errors = result.error.issues.map((issue) => {
            const error = new jsonschema_1.default.ValidationError(issue.message, issue, undefined, issue.path.join('.'), issue.path.join('.'), undefined);
            return error;
        });
        onErrors(errors);
    }
    return result.success;
};
exports.zodTypeguardFunctionWrapper = zodTypeguardFunctionWrapper;
/**
 * Wrapper for Zod schema that matches the interface of typeguardFunctionLogWrapper.
 * Use this when migrating from jsonschema to Zod typeguards.
 */
const zodTypeguardFunctionLogWrapper = (params) => (obj, logInputs) => {
    const { schema, typeguardName } = params;
    const result = schema.safeParse(obj);
    if (!result.success && logInputs?.logger) {
        const { logger, logLevel, metadata } = logInputs;
        const errorMessages = result.error.issues
            .map((issue) => {
            const path = issue.path.join('.');
            return `${path ? `${path}: ` : ''}${issue.message}`;
        })
            .join(', ');
        logger({
            message: `"${typeguardName}" typeguard incurred ${result.error.issues.length} errors: ${errorMessages}`,
            metadata: { logMeta: { ...metadata } },
        }, logLevel || Log_1.LogLevel.Error);
    }
    return result.success;
};
exports.zodTypeguardFunctionLogWrapper = zodTypeguardFunctionLogWrapper;
/**
 * Zod version of typeguardErrorWrapper.
 * This util just returns back the associated typeguard errors as strings.
 */
const zodTypeguardErrorWrapper = (params) => (obj) => {
    const { schema } = params;
    const result = schema.safeParse(obj);
    if (result.success) {
        return [];
    }
    return result.error.issues.map((issue) => {
        const path = issue.path.join('.');
        return `${path ? `${path}: ` : ''}${issue.message}`;
    });
};
exports.zodTypeguardErrorWrapper = zodTypeguardErrorWrapper;
/**
 * Wrapper for Zod array schema that matches the interface of typeguardArrayFunctionLogWrapper.
 * Use this when migrating from jsonschema to Zod typeguards.
 */
const zodTypeguardArrayFunctionLogWrapper = (params) => (obj, logInputs) => {
    const { schema, typeguardName } = params;
    if (!Array.isArray(obj)) {
        if (logInputs?.logger) {
            const errObj = {
                message: `"${typeguardName}" typeguard expected array input`,
            };
            logInputs.logger(errObj, logInputs.logLevel || Log_1.LogLevel.Error);
        }
        return false;
    }
    const allErrors = [];
    obj.forEach((item, index) => {
        const result = schema.safeParse(item);
        if (!result.success) {
            allErrors.push({ index, issues: result.error.issues });
        }
    });
    if (allErrors.length > 0 && logInputs?.logger) {
        const { logger, logLevel, metadata } = logInputs;
        const errorMessages = allErrors
            .flatMap(({ index, issues }) => issues.map((issue) => {
            const path = issue.path.join('.');
            return `[${index}]${path ? `.${path}` : ''}: ${issue.message}`;
        }))
            .join(', ');
        logger({
            message: `"${typeguardName}" typeguard incurred ${allErrors.length} errors: ${errorMessages}`,
            metadata: { logMeta: { ...metadata } },
        }, logLevel || Log_1.LogLevel.Error);
    }
    return allErrors.length === 0;
};
exports.zodTypeguardArrayFunctionLogWrapper = zodTypeguardArrayFunctionLogWrapper;
/**
 * ============================================================================
 * ZOD DATETIME SCHEMA UTILITIES
 * ============================================================================
 * These utilities help handle Date fields in Zod schemas that need to:
 * 1. Accept both ISO strings (from wire/JSON) and Date objects (in domain)
 * 2. Generate proper JSON Schema with format: "date-time" for Python codegen
 *
 * Usage:
 * - Use `zodDateTimeSchema` in your main schema for runtime parsing (outputs Date)
 * - Use `zodDateTimeWireSchema` in wire schemas for JSON schema generation (outputs string)
 *
 * Example:
 *   const MyTypeSchema = z.object({
 *     created_at: zodDateTimeSchema,
 *     updated_at: zodDateTimeSchema,
 *   });
 *
 *   // For JSON schema generation, use wire version:
 *   const MyTypeWireSchema = z.object({
 *     created_at: zodDateTimeWireSchema,
 *     updated_at: zodDateTimeWireSchema,
 *   });
 */
/**
 * Wire schema for datetime fields - validates ISO datetime strings.
 * Use this schema for JSON schema generation (Python codegen).
 * Output type: string
 */
exports.zodDateTimeWireSchema = zod_1.z.iso.datetime();
/**
 * Domain schema for datetime fields - accepts ISO strings or Date objects,
 * outputs JavaScript Date objects.
 * Use this schema for runtime validation and parsing.
 * Output type: Date
 */
exports.zodDateTimeSchema = zod_1.z
    .preprocess((val) => (val instanceof Date ? val.toISOString() : val), zod_1.z.iso.datetime())
    .transform((val) => new Date(val));
//# sourceMappingURL=typeguardUtils.js.map